import { useState } from "react";
import styled from "styled-components";
import { useAppSelector } from "../../../app/hooks";
import { deliveryState } from "../../../app/reducers/delivery-slice";
import theme from "../../../style/theme";
import { DeliveryGroupType } from "../../../types/schedule/delivery-group-type";
import useDidMountEffect from "../../../utils/custom-hook/use-did-mount-effect";
import {
  convertToTimeLimited,
  getHour,
  getMidday,
  getMinute,
} from "../../../utils/date/date-format";
import { DeliveryPayloadValidator } from "../../../utils/validator/delivery";
import { SearchHour } from "../../autocomplete/SearchHour";
import { SearchMinute } from "../../autocomplete/SearchMinute";
import { CustomRadioButton } from "../../button/CustomRadioButton";
import { SwitchButton } from "../../button/SwitchButton";
import { GeneralTextField } from "../../text-field/GeneralTextField";
import { ActionForm, LoadForm, WorkPlaceForm } from "./DeliveryDialog";
import { NewLocationSearchCard } from "./NewLocationSearchCard";
import { RecentLocationSearchCard } from "./RecentLocationSearchCard";
import Asterisk from "../../asterisk/asterisk";

interface Props {
  selectedClientId: string;
  actionForm: ActionForm;
  addressForm: string;
  workPlaceForm: WorkPlaceForm;
  loadForm: LoadForm;
  deliveryPayloadValidator: DeliveryPayloadValidator;
  dateToWork: Date;
  setActionForm: React.Dispatch<React.SetStateAction<ActionForm>>;
  setAddressForm: React.Dispatch<React.SetStateAction<string>>;
  setWorkPlaceForm: React.Dispatch<React.SetStateAction<WorkPlaceForm>>;
  setLoadForm: React.Dispatch<React.SetStateAction<LoadForm>>;
}

export interface TimeForm {
  type: string;
  hour: string;
  minute: string;
}

const radioMap = {
  "기존 장소": "기존 장소",
  신규입력: "신규입력",
};

export const DropOffActionFormCard: React.FC<Props> = ({
  selectedClientId,
  actionForm,
  addressForm,
  workPlaceForm,
  loadForm,
  deliveryPayloadValidator,
  dateToWork,
  setActionForm,
  setAddressForm,
  setWorkPlaceForm,
  setLoadForm,
}) => {
  const { selectedDelivery } = useAppSelector(deliveryState);
  const [selectedKey, setSelectedKey] = useState("기존 장소");
  const [timeLimited, setTimeLimited] = useState({
    type: selectedDelivery
      ? getMidday(selectedDelivery.dropOffAction.startTimeLimited)
      : "오전",
    hour: selectedDelivery
      ? getHour(selectedDelivery.dropOffAction.startTimeLimited)
      : "",
    minute: selectedDelivery
      ? getMinute(selectedDelivery.dropOffAction.startTimeLimited)
      : "",
  });

  const handleClickTimeLimitedTypeSwitchButton = (value: string) => {
    setTimeLimited({
      ...timeLimited,
      type: value,
    });
  };

  const setTimeLimitedHourForm = (newHour: string) => {
    setTimeLimited({
      ...timeLimited,
      hour: newHour,
    });
  };

  const setTimeLimitedMinuteForm = (newMinute: string) => {
    setTimeLimited({
      ...timeLimited,
      minute: newMinute,
    });
  };

  const handleChangeActionFormTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setActionForm({
      ...actionForm,
      [event.target.name]: event.target.value,
    });
  };

  const handleChangeLoadFormTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLoadForm({
      ...loadForm,
      [event.target.name]: event.target.value === "" ? undefined : event.target.value,
    });
  };

  useDidMountEffect(() => {
    if (timeLimited.hour === "" || isNaN(Number(timeLimited.minute))) {
      return;
    }

    setActionForm({
      ...actionForm,
      startTimeLimited: convertToTimeLimited(
        timeLimited,
        dateToWork
      ).toISOString(),
      endTimeLimited: convertToTimeLimited(
        timeLimited,
        dateToWork
      ).toISOString(),
    });
  }, [timeLimited, dateToWork]);

  return (
    <LocationCardContainer>
      <LocationCardTitle>전달지 정보</LocationCardTitle>
      <FlexContainer>
        <FieldContainer>요청 시간<Asterisk/></FieldContainer>
        <SwitchButtonContainer>
          <SwitchButton
            selectTitle="오전"
            active={timeLimited.type === "오전"}
            value={"오전"}
            leftBorderRadius
            onClick={handleClickTimeLimitedTypeSwitchButton}
            width="3.625rem"
          />
          <SwitchButton
            selectTitle="오후"
            active={timeLimited.type === "오후"}
            value={"오후"}
            rightBorderRadius
            onClick={handleClickTimeLimitedTypeSwitchButton}
            width="3.625rem"
          />
        </SwitchButtonContainer>
        <TimeSelectorContainer>
          <SearchHour
            hour={timeLimited.hour}
            setHour={setTimeLimitedHourForm}
          />
        </TimeSelectorContainer>
        <UnitLabel>시</UnitLabel>
        <TimeSelectorContainer>
          <SearchMinute
            minute={timeLimited.minute}
            setMinute={setTimeLimitedMinuteForm}
          />
        </TimeSelectorContainer>
        <UnitLabel>분</UnitLabel>
      </FlexContainer>
      <FlexContainer>
        <FieldContainer>전달 장소<Asterisk/></FieldContainer>
        <RadioButtonContainer>
          <CustomRadioButton
            radioMap={radioMap}
            selectedKey={selectedKey}
            setSelectedKey={setSelectedKey}
            name={"drop_off"}
          />
        </RadioButtonContainer>
      </FlexContainer>
      <NoMarginFlexContainer>
      {selectedKey === "신규입력" ? (
        <NewLocationSearchCardContainer>
          <NewLocationSearchCard
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            workPlaceForm={workPlaceForm}
            setWorkPlaceForm={setWorkPlaceForm}
            deliveryPayloadValidatorAddress={
              deliveryPayloadValidator.dropOffAddress
            }
            deliveryPayloadValidatorDetail={
              deliveryPayloadValidator.dropOffWorkPlaceDetail
            }
          />
        </NewLocationSearchCardContainer>
      ) : (
        <RecentLocationSearchCardContainer>
          <RecentLocationSearchCard
            clientId={selectedClientId}
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            workPlaceForm={workPlaceForm}
            setWorkPlaceForm={setWorkPlaceForm}
            deliveryPayloadValidatorAddress={
              deliveryPayloadValidator.dropOffAddress
            }
            deliveryPayloadValidatorDetail={
              deliveryPayloadValidator.dropOffWorkPlaceDetail
            }
            type={DeliveryGroupType.DropOff}
          />
        </RecentLocationSearchCardContainer>
      )}
    </NoMarginFlexContainer>
      <NoMarginFlexContainer>
        <FieldContainer>
          물품정보
          <Asterisk/>
        </FieldContainer>
        <GeneralTextField
          placeholder="물품명 (또는 수취인)"
          name="name"
          value={loadForm.name}
          onChange={handleChangeLoadFormTextField}
          width="170px"
        />
        <Divider />
        <GeneralTextField
          name="quantity"
          value={loadForm.quantity}
          onChange={handleChangeLoadFormTextField}
          width="79px"
        />
        <UnitLabel>개(수량 선택)</UnitLabel>
      </NoMarginFlexContainer>
      <FlexContainer>
        <FieldContainer>요청사항</FieldContainer>
        <GeneralTextField
          placeholder="배송원이 확인할 고객 요청사항을 입력하세요"
          name="requirement"
          value={actionForm.requirement}
          onChange={handleChangeActionFormTextField}
          width="410px"
        />
      </FlexContainer>
    </LocationCardContainer>
  );
};

const LocationCardContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LocationCardTitle = styled.div`
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: ${theme.palette.primitives.black};
  margin-bottom: 1.25rem;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const NoMarginFlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FieldContainer = styled.div`
  width: 88px;
  font-style: normal;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
`;

const RadioButtonContainer = styled.div``;

const SwitchButtonContainer = styled.div`
  display: flex;
  width: 7.25rem;
  margin: 0 0.75rem 0 0;
`;

const TimeSelectorContainer = styled.div`
  margin-left: 0.75rem;
`;

const UnitLabel = styled.div`
  margin-left: 0.75rem;
  font-weight: 500;
  font-size: 0.938rem;
  line-height: 1.125rem;
  color: ${theme.palette.primitives.gray600};
`;

const RecentLocationSearchCardContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const NewLocationSearchCardContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;

const Divider = styled.div`
  width: 1px;
  height: 2.125rem;
  margin: 0 1rem;
  background: ${theme.palette.primitives.gray200};
`;
